import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "../../context/LanguageContext";
import { api } from "../../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Calendar } from "../ui/calendar";
import { format } from "date-fns";
import { enUS, de } from "date-fns/locale";
import { toast } from "sonner";

export default function BookingWidget() {
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [units, setUnits] = useState([]);
  const [unitId, setUnitId] = useState("zirben");
  const [range, setRange] = useState();
  const [guests, setGuests] = useState(2);
  const [pets, setPets] = useState(0);
  const [loading, setLoading] = useState(false);
  const [quote, setQuote] = useState(null);
  const [bookedDates, setBookedDates] = useState([]);

  useEffect(() => {
    api.get("/units").then(({ data }) => setUnits(data.units || [])).catch(() => {});
  }, []);

  useEffect(() => {
    api.get(`/availability/booked-dates?unit_id=${unitId}`).then(({ data }) => {
      setBookedDates((data?.dates || []).map((d) => new Date(d)));
    }).catch(() => {});
  }, [unitId]);

  const activeUnit = units.find((u) => u.id === unitId);
  const maxG = activeUnit?.max_guests || 8;
  const maxP = activeUnit?.max_pets ?? 2;

  useEffect(() => {
    if (range?.from && range?.to) {
      api.post("/quote", {
        check_in: format(range.from, "yyyy-MM-dd"),
        check_out: format(range.to, "yyyy-MM-dd"),
        guests, pets, unit_id: unitId,
      }).then(({ data }) => setQuote(data)).catch(() => setQuote(null));
    } else { setQuote(null); }
  }, [range, guests, pets, unitId]);

  useEffect(() => { if (pets > maxP) setPets(maxP); }, [maxP, pets]);

  const submit = async () => {
    if (!range?.from || !range?.to) { toast.error(lang === "de" ? "Bitte Zeitraum wählen" : "Please pick a date range"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/availability", {
        check_in: format(range.from, "yyyy-MM-dd"),
        check_out: format(range.to, "yyyy-MM-dd"),
        unit_id: unitId,
      });
      if (!data.available) { toast.error(lang === "de" ? "Zeitraum bereits gebucht" : "Dates not available"); return; }
      const params = new URLSearchParams({
        ci: format(range.from, "yyyy-MM-dd"),
        co: format(range.to, "yyyy-MM-dd"),
        g: String(guests),
        p: String(pets),
        unit: unitId,
      });
      nav(`/book?${params.toString()}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };

  const locale = lang === "de" ? de : enUS;
  const fmt = (d) => d ? format(d, "dd MMM yyyy", { locale }) : "—";

  return (
    <div data-testid="booking-widget" className="bg-[hsl(var(--card))] shadow-[0_24px_60px_rgba(0,0,0,0.45)] border border-[hsl(var(--foreground))]/12">
      {/* Unit tabs — 3 columns: zirben, landhaus, combo highlighted */}
      <div className="grid grid-cols-3 border-b border-[hsl(var(--foreground))]/10">
        {units.map((u) => {
          const isCombo = u.id === "combo";
          const active = unitId === u.id;
          return (
            <button
              key={u.id}
              onClick={() => setUnitId(u.id)}
              data-testid={`widget-unit-${u.id}`}
              className={`relative px-4 md:px-6 py-4 text-left transition-colors ${active ? "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]" : "bg-transparent text-[hsl(var(--foreground))] hover:bg-[hsl(var(--foreground))]/5"}`}
            >
              {isCombo && (
                <span className="absolute top-2 right-2 bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] text-[10px] tracking-[0.18em] font-semibold uppercase px-2 py-0.5">−10%</span>
              )}
              <div className={`text-[10px] tracking-[0.22em] uppercase font-semibold ${active ? "text-[hsl(var(--accent-foreground))]/80" : "text-[hsl(var(--accent))]"}`}>
                {isCombo ? (lang === "de" ? "Combo · 20 Gäste" : "Combo · 20 guests") : (lang === "de" ? "Unterkunft" : "Stay")}
              </div>
              <div className="font-serif text-lg md:text-xl lg:text-2xl mt-1 leading-tight">
                {isCombo ? (lang === "de" ? "Beide Häuser" : "Both Houses") : u.name}
              </div>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 items-stretch divide-y md:divide-y-0 md:divide-x divide-white/10">
        <Popover>
          <PopoverTrigger asChild>
            <button data-testid="widget-dates" className="md:col-span-5 text-left px-6 py-5 hover:bg-[hsl(var(--foreground))]/5 transition-colors">
              <div className="overline">{t.widget.checkin} — {t.widget.checkout}</div>
              <div className="mt-2 font-serif text-xl md:text-2xl text-[hsl(var(--foreground))]">
                {fmt(range?.from)} <span className="text-[hsl(var(--accent))]">→</span> {fmt(range?.to)}
              </div>
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="!rounded-none p-0 border border-[hsl(var(--foreground))]/15 shadow-2xl bg-[hsl(var(--card))]">
            <Calendar
              mode="range"
              selected={range}
              onSelect={setRange}
              numberOfMonths={2}
              locale={locale}
              disabled={[{ before: new Date() }, ...bookedDates]}
              data-testid="widget-calendar"
            />
          </PopoverContent>
        </Popover>

        <Popover>
          <PopoverTrigger asChild>
            <button data-testid="widget-guests" className="md:col-span-2 text-left px-6 py-5 hover:bg-[hsl(var(--foreground))]/5 transition-colors">
              <div className="overline">{t.widget.guests}</div>
              <div className="mt-2 font-serif text-xl md:text-2xl text-[hsl(var(--foreground))]">
                {guests}
              </div>
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="!rounded-none p-4 w-56 border border-[hsl(var(--foreground))]/15 shadow-2xl bg-[hsl(var(--card))] text-[hsl(var(--foreground))]">
            <div className="flex items-center justify-between">
              <span className="text-sm">{t.widget.guests}</span>
              <div className="flex items-center gap-3">
                <button data-testid="guests-minus" onClick={() => setGuests((g) => Math.max(1, g - 1))} className="w-8 h-8 border border-[hsl(var(--foreground))]/20 hover:bg-[hsl(var(--foreground))]/10">−</button>
                <span data-testid="guests-count" className="w-6 text-center">{guests}</span>
                <button data-testid="guests-plus" onClick={() => setGuests((g) => Math.min(maxG, g + 1))} className="w-8 h-8 border border-[hsl(var(--foreground))]/20 hover:bg-[hsl(var(--foreground))]/10">+</button>
              </div>
            </div>
            <div className="mt-2 text-[11px] text-[hsl(var(--foreground))]/78">max {maxG}</div>
          </PopoverContent>
        </Popover>

        <Popover>
          <PopoverTrigger asChild>
            <button data-testid="widget-pets" className="md:col-span-2 text-left px-6 py-5 hover:bg-[hsl(var(--foreground))]/5 transition-colors">
              <div className="overline">{t.widget.pets}</div>
              <div className="mt-2 font-serif text-xl md:text-2xl text-[hsl(var(--foreground))]">
                {pets}
              </div>
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="!rounded-none p-4 w-56 border border-[hsl(var(--foreground))]/15 shadow-2xl bg-[hsl(var(--card))] text-[hsl(var(--foreground))]">
            <div className="flex items-center justify-between">
              <span className="text-sm">{t.widget.pets}</span>
              <div className="flex items-center gap-3">
                <button data-testid="pets-minus" onClick={() => setPets((p) => Math.max(0, p - 1))} className="w-8 h-8 border border-[hsl(var(--foreground))]/20 hover:bg-[hsl(var(--foreground))]/10">−</button>
                <span data-testid="pets-count" className="w-6 text-center">{pets}</span>
                <button data-testid="pets-plus" onClick={() => setPets((p) => Math.min(maxP, p + 1))} className="w-8 h-8 border border-[hsl(var(--foreground))]/20 hover:bg-[hsl(var(--foreground))]/10">+</button>
              </div>
            </div>
            <div className="mt-2 text-[11px] text-[hsl(var(--foreground))]/78">max {maxP} · €35 {lang === "de" ? "pro Tier" : "per pet"}</div>
          </PopoverContent>
        </Popover>

        <div className="md:col-span-3 flex">
          <button
            data-testid="widget-submit"
            onClick={submit}
            disabled={loading}
            className="w-full bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] text-[12px] tracking-[0.2em] uppercase font-semibold hover:bg-white hover:text-[hsl(var(--primary))] transition-colors px-6 py-5"
          >
            {loading ? t.widget.checking : t.widget.submit}
          </button>
        </div>
      </div>

      {quote && (
        <div data-testid="widget-quote" className="border-t border-[hsl(var(--foreground))]/10 bg-[hsl(var(--moss))] px-6 py-5">
          <div className="flex flex-wrap items-end justify-between gap-x-8 gap-y-3">
            <div className="text-sm text-[hsl(var(--foreground))]/88 space-y-1">
              <div><span className="text-[hsl(var(--foreground))] font-medium">{quote.unit_name}</span> · {quote.nights} {t.widget.nights}</div>
              {quote.summer_nights > 0 && (
                <div>
                  {lang === "de" ? "Sommer" : "Summer"}: {quote.summer_nights} × €{quote.summer_rate.toFixed(0)} = €{quote.summer_subtotal.toFixed(0)}
                </div>
              )}
              {quote.winter_nights > 0 && (
                <div>
                  {lang === "de" ? "Winter" : "Winter"}: {quote.winter_nights} × €{quote.winter_rate.toFixed(0)} = €{quote.winter_subtotal.toFixed(0)}
                </div>
              )}
              <div>{t.widget.cleaning}: €{quote.cleaning_fee.toFixed(0)}</div>
              {quote.pets > 0 && (
                <div>{t.widget.pet_fee}: {quote.pets} × €{quote.pet_fee_per_animal.toFixed(0)} = €{quote.pet_fee_total.toFixed(0)}</div>
              )}
            </div>
            <div className="text-right">
              <div className="overline">{t.widget.total}</div>
              <div className="font-serif text-3xl text-[hsl(var(--accent))] leading-none mt-1">€{quote.total.toFixed(0)}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
